import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';
import { persistor } from '../store';
import WebSocketManager from '../../websocket/WebSocketManager';

export const login = createAsyncThunk(
  '/login',
  async ({ email, password, userType }, { rejectWithValue }) => {
    try {
      const headers = {
        'X-Admin-Request': userType === 'admin' ? 'true' : 'false',
      };

      const response = await api.post('/login', 
        { email, password, user_type: userType },
        { headers }
      );

      console.log('Login response:', response.data);
      
      const payload = JSON.parse(atob(response.data.access_token.split('.')[1]));
      console.log('Token payload:', payload);

      if (userType === 'admin' && payload.sub.role !== 'system_admin') {
        throw new Error('Unauthorized access attempt');
      }

      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      return rejectWithValue(
        error.response?.data || { message: 'Authentication failed' }
      );
    }
  }
);

export const refreshToken = createAsyncThunk(
  '/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { refreshToken } = getState().auth;
      const response = await api.post('/refresh', null, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await api.post('/logout');
      WebSocketManager.disconnect(true);
      dispatch(resetAuthState());
      persistor.purge();
    } catch (error) {
      console.error('Logout failed:', error);
      return rejectWithValue(error.response?.data || 'Logout failed');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (profileData, { getState, rejectWithValue }) => {
    try {
      // Clean up the addresses data before sending
      if (profileData.addresses) {
        profileData.addresses = profileData.addresses.filter(addr => {
          // Check if all required fields are present and non-empty
          const requiredFields = ['street_address', 'city', 'state', 'zip_code'];
          return requiredFields.every(field => addr[field]?.trim());
        });

        // If no valid addresses after filtering, remove the addresses field
        if (profileData.addresses.length === 0) {
          delete profileData.addresses;
        }
      }

      const response = await api.put('/api/users/profile', profileData);
      
      // Get the refresh token from the state
      const { refreshToken } = getState().auth;
      
      // Force a token refresh with proper authorization
      const refreshResponse = await api.post('/refresh', null, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`
        }
      });
      
      return {
        profile: response.data,
        tokens: refreshResponse.data
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update profile');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    userType: null,
    organization: null,
  },
  reducers: {
    resetAuthState: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.userType = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.accessToken = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        state.isAuthenticated = true;

        const payload = JSON.parse(atob(action.payload.access_token.split('.')[1]));
        console.log('Decoded payload in fulfilled:', payload);
        
        state.user = {
          id: payload.sub.user_id,
          role: payload.sub.role,
          firstName: payload.sub.first_name,
          lastName: payload.sub.last_name,
          phone: payload.sub.phone,
          email: payload.sub.email,
          entityId: payload.sub.entity_id,
          companyName: payload.sub.company_name,
          isBroker: payload.sub.is_broker || false,
          isViewOnly: payload.sub.is_view_only || false,
          customerType: payload.sub.customer_type || null,
          customer: payload.sub.role.startsWith('customer') ? {
            is_broker: payload.sub.is_broker || false,
            is_view_only: payload.sub.is_view_only || false,
            customer_type: payload.sub.customer_type || null
          } : null
        };
        
        state.organization = payload.sub.organization;
        state.userType = payload.sub.role;
        
        console.log('Updated state:', state);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'An error occurred during login.';
        state.isAuthenticated = false;
        console.error('Login rejected:', action.payload);
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.accessToken = action.payload.access_token;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.user = null;
        state.error = 'Session expired. Please log in again.';
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.userType = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.payload || 'Logout failed';
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        // Update user profile data
        state.user = { ...state.user, ...action.payload.profile };
        
        // Update tokens and decode new organization data
        state.accessToken = action.payload.tokens.access_token;
        
        // Decode and update organization data from new token
        const payload = JSON.parse(atob(action.payload.tokens.access_token.split('.')[1]));
        if (payload.sub.organization) {
          state.organization = payload.sub.organization;
        }
        
        state.status = 'succeeded';
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUserType = (state) => state.auth.userType;
export const selectUserEntityId = (state) => state.auth.user?.entityId;
export const getFirstName = (state) => state.auth.user?.firstName;
export const getCompanyName = (state) => state.auth.user?.companyName;
export const selectUserId = (state) => state.auth.user?.id;
export const selectOrganizationAddresses = (state) => state.auth.organization?.addresses || [];
export const selectIsBroker = (state) => state.auth.user?.customer?.is_broker || false;

export const { resetAuthState } = authSlice.actions;
export default authSlice.reducer;
